import {comboGroups} from "../../../shared/service-combo";
import {servicePurchaseCopy} from "../../../shared/service-purchase-copy";
import type {ServiceComboItem} from "@/lib/service-commerce-context";
import {purchaseErrorMessage} from "../../../shared/service-purchase-copy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useServiceCommerce } from "@/lib/service-commerce-context";
import { formatServiceEur as formatEur } from "@/lib/format";
import { Cpu, Loader2, Minus, ShoppingBasket, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import { localizedPath } from "@/lib/routes";
import { Link } from "react-router";
import { useAction } from "convex/react";
import { api } from "@/convex/_generated/api";
import AgreementReviewDialog from "@/pages/services/_components/agreement-review-dialog";
import type { BuyerInput } from "@/pages/services/_components/agreement-review-dialog";
import { toast } from "sonner";

export default function ServiceCommerceDock() {
  const { t, i18n } = useTranslation("services");
  const { user, signinRedirect } = useAuth();
  const {
    items,
    removeItem,
    clear,
    open,
    setOpen,
    balanceMicrochips,
    checkout,
    checkoutLoading,
  } = useServiceCommerce();
  const [rewardInput, setRewardInput] = useState("0");
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [agreementLoading, setAgreementLoading] = useState(false);
  const [agreementDraft, setAgreementDraft] = useState<{ text: string; sha256: string } | null>(null);
  const previewAgreement = useAction(api.payments.stripe.previewServiceBundleAgreement);
  const locale = i18n.resolvedLanguage ?? "it";
  const [selection, setSelection] = useState<ServiceComboItem[]>([]);
  const groups = comboGroups(items);
  const period = (months: number) => months === 1 ? t("combo.month_short") : (servicePurchaseCopy[locale as keyof typeof servicePurchaseCopy] ?? servicePurchaseCopy.it).period.replace("{{count}}",String(months));
  const totals = useMemo(
    () => ({
      setup: items.reduce((sum, item) => sum + (item.setupAmount ?? 0), 0),
      monthly: items.reduce((sum, item) => sum + (item.monthlyAmount ?? 0), 0),
    }),
    [items],
  );
  const chargeSelection = selection.length ? selection : items;
  const selectedDeferred = chargeSelection.some(item=>item.setupAmount !== undefined && item.monthlyAmount !== undefined);
  const initialChargeCents = Math.round(chargeSelection.reduce((sum,item)=>sum+(item.setupAmount??0)+(selectedDeferred?0:(item.monthlyAmount??0)),0)*100);
  const maxRedeemableMicrochips = Math.min(
    balanceMicrochips,
    Math.max(0, initialChargeCents - 50),
  );
  const requestedRewards = Math.min(
    maxRedeemableMicrochips,
    Math.max(0, Math.floor(Number(rewardInput) || 0)),
  );

  const previewForBuyer = (buyer: BuyerInput) => previewAgreement({
    items: selection.map(({ serviceKey, offerVariant, voucherCode }) => ({ serviceKey, offerVariant, voucherCode: voucherCode?.trim() || undefined })),
    locale,
    buyer,
  });

  const reviewAgreement = async (selected: ServiceComboItem[]) => {
    setSelection(selected);
    if (!user) {
      toast.info(t("buy.signin_title"), { description: t("buy.signin_desc") });
      await signinRedirect(window.location.href);
      return;
    }
    setAgreementLoading(true);
    try {
      const draft = {text:"",sha256:""};
      setAgreementDraft(draft);
      setAgreementOpen(true);
    } catch (error) {
      toast.error(t("buy.error_title"), { description: purchaseErrorMessage(error,t("buy.error_generic")) });
    } finally {
      setAgreementLoading(false);
    }
  };

  if (!user && items.length === 0) return null;

  return (
    <>
    <aside data-enkey-commerce-dock className="fixed top-[var(--enkey-floating-top,112px)] right-[max(1rem,env(safe-area-inset-right))] z-40 flex max-w-[calc(100vw-2rem)] flex-col-reverse items-end gap-2">
      {open && (
        <div className="max-h-[calc(100svh-var(--enkey-floating-top,112px)-5rem)] overflow-y-auto w-[min(24rem,calc(100vw-2rem))] rounded-2xl border border-primary/20 bg-white/95 p-4 text-slate-900 shadow-2xl backdrop-blur dark:border-primary/25 dark:bg-slate-950/95 dark:text-slate-100">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-display text-lg font-bold">
                {t("combo.title")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("combo.count", { count: items.length })}
              </p>
              {user && (
                <>
                  <p className="text-xs text-muted-foreground">
                    {t("wallet.value", {
                      chips: (balanceMicrochips / 10).toLocaleString(locale),
                      euros: formatEur(balanceMicrochips / 100, locale),
                    })}
                  </p>
                  <Link
                    to={localizedPath("terms", locale)}
                    className="text-xs font-medium text-primary underline-offset-2 hover:underline dark:text-primary"
                  >
                    {t("wallet.how_it_works")}
                  </Link>
                </>
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => setOpen(false)}
              aria-label={t("combo.close")}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {items.length === 0 ? (
            <p className="mt-4 rounded-xl bg-muted/60 p-3 text-sm text-muted-foreground">
              {t("combo.empty")}
            </p>
          ) : (
            <div className="mt-4 max-h-48 space-y-2 overflow-y-auto">
              {items.map((item) => (
                <div
                  key={`${item.serviceKey}:${item.offerVariant}`}
                  className="flex items-center justify-between gap-3 rounded-xl border p-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">
                      {item.label}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {item.setupAmount !== undefined &&
                        formatEur(item.setupAmount, locale)}
                      {item.setupAmount !== undefined &&
                        item.monthlyAmount !== undefined &&
                        " + "}
                      {item.monthlyAmount !== undefined &&
                        `${formatEur(item.monthlyAmount, locale)} / ${period(item.recurringMonths ?? 1)}`}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() =>
                      removeItem(item.serviceKey, item.offerVariant)
                    }
                    aria-label={t("combo.remove", { label: item.label })}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {items.length > 0 && (
            <>
              <div className="mt-4 grid grid-cols-2 gap-2 rounded-xl bg-muted/60 p-3 text-sm">
                <span>{t("combo.setup_total")}</span>
                <strong className="text-right">
                  {formatEur(totals.setup, locale)}
                </strong>
                {groups.filter(group=>group.months>0).map(group=><div className="col-span-2 flex justify-between gap-2" key={group.months}><span>{period(group.months)}</span><strong>{formatEur(group.items.reduce((sum,item)=>sum+(item.monthlyAmount??0),0),locale)}</strong></div>)}
              </div>
              {balanceMicrochips > 0 && groups.length === 1 && (
                <label className="mt-3 block text-xs font-medium">
                  {t("wallet.use", { balance: balanceMicrochips })}
                  <Input
                    type="number"
                    min={0}
                    max={maxRedeemableMicrochips}
                    step={1}
                    value={rewardInput}
                    onChange={(event) => setRewardInput(event.target.value)}
                    className="mt-1"
                  />
                </label>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={clear}>
                  {t("combo.clear")}
                </Button>
                {groups.map(group=><Button
                  key={group.months}
                  type="button"
                  className="flex-1"
                  disabled={checkoutLoading || agreementLoading}
                  onClick={() => void reviewAgreement(group.items)}
                >
                  {checkoutLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ShoppingBasket className="h-4 w-4" />
                  )}
                  {t("combo.checkout")}{groups.length>1 && ` · ${group.months ? period(group.months) : t("combo.setup_total")}`}
                </Button>)}
              </div>
              {groups.length > 1 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {({it:"I servizi con scadenze diverse si acquistano separatamente.",en:"Services with different billing periods are purchased separately.",es:"Los servicios con períodos distintos se compran por separado.",fr:"Les services avec des échéances différentes s’achètent séparément.",de:"Leistungen mit unterschiedlichen Abrechnungszeiträumen werden separat gekauft."} as Record<string,string>)[locale] ?? "Services with different billing periods are purchased separately."}
                </p>
              )}
            </>
          )}
        </div>
      )}

      <button
        type="button"
        className="relative grid min-h-12 grid-cols-[auto_1fr] items-center gap-x-2 gap-y-0.5 rounded-full border border-primary/30 bg-white/95 px-4 py-3 text-left text-slate-900 shadow-[0_16px_44px_rgba(15,23,42,0.2)] backdrop-blur transition-colors hover:border-primary/50 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:border-primary/30 dark:bg-slate-950/95 dark:text-slate-100 dark:hover:border-primary/50 dark:hover:bg-slate-950 dark:focus-visible:ring-primary"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <Cpu className="row-span-2 h-5 w-5 text-primary dark:text-primary" />
        <strong className="text-sm leading-none">
          {t("wallet.microchips", { count: balanceMicrochips })}
        </strong>
        <span className="text-[0.68rem] leading-tight text-slate-500 dark:text-slate-400">
          {t("wallet.rate")}
        </span>
        {items.length > 0 && (
          <>
            <span
              className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white shadow-sm dark:bg-primary dark:text-slate-950"
              aria-hidden="true"
            >
              {items.length}
            </span>
            <span className="sr-only">
              {t("combo.count", { count: items.length })}
            </span>
          </>
        )}
      </button>
    </aside>
    {agreementDraft && <AgreementReviewDialog serviceKeys={selection.map(i=>i.serviceKey)} key={agreementDraft.sha256} open={agreementOpen} onOpenChange={setAgreementOpen} text={agreementDraft.text} sha256={agreementDraft.sha256} loading={checkoutLoading} locale={locale} onRefresh={previewForBuyer} onProceed={(sha256, buyer) => void checkout(groups.length === 1 ? requestedRewards : 0, locale, sha256, buyer, selection)} />}
    </>
  );
}
